<template>
  <v-autocomplete
      v-bind="$attrs"
      :value="value"
      @input="onInput($event)"
      :loading="loading"
      :items="items"
      :search-input.sync="searchText"
      flat
      :item-text="itemText"
      :item-value="itemValue"
      :filter="filterSelectedValues"
      @click="loadIfLazy"
      @focus="loadIfLazy"
      :clearable="clearable"
      @click:clear="onClear"
      :multiple="multiple"
      chips
      :deletable-chips="multiple"
      dense
  >

    <template v-slot:no-data>
      <v-list-item>
        <v-list-item-title>
          <i v-if="loading">{{ trans('general.isLoading') }}</i>
          <i v-else>{{ trans('general.noData') }}</i>
        </v-list-item-title>
      </v-list-item>
    </template>

    <template v-slot:append-item v-if="moreItemsAvailable">
      <v-list-item>
        <v-list-item-title>
          <i class="caption">{{ trans('general.moreAvailableWithSearch', {numberOfAvailableItems: numberOfTotalItems - pageSize}) }}</i>
        </v-list-item-title>
      </v-list-item>
    </template>

  </v-autocomplete>
</template>

<script>

import {LocaleUtils} from '@/utils/locale-utils';
import {and, comparison, or, inList} from 'rsql-builder';
import {FilterUtils} from '@/utils/filter-utils';

export default {
  name: 'AjaxSelect',
  mixins: [
    LocaleUtils,
    FilterUtils
  ],
  props: {
    itemText: {
      default() { return 'name'; }
    },
    itemValue: {
      default() { return 'id'; }
    },
    resourceUrl: {
      required: true,
      default() { return null; }
    },
    filterAttributes: {
      type: Array,
      default() {
        return [(this.itemText && (typeof this.itemText === 'string'))  ? this.itemText : 'name'];
      }
    },
    filterExtra: {
      type: Array,
      default() {
        return [];
      }
    },
    eagerLoading: {
      type: Boolean,
      default() { return false; }
    },
    value: {
      default() { return null; }
    },
    multiple: {
      type: Boolean,
      default() { return false; }
    },
    pageSize: {
      type: Number,
      default() { return 25; }
    },

    // defaults for vuetify
    clearable: {
      type: Boolean,
      default() { return true;}
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      searchText: '',
      searchTimer: null,
      moreItemsAvailable: false,
      numberOfTotalItems: null,
    }
  },
  computed: {
    filter() {
      let filter = null;

      let filterSearchText = this.buildSearchFilter(this.searchText, this.filterAttributes);

      let filterExtra = null;
      if (this.filterExtra) {
        filterExtra = this.buildFilterString(this.filterExtra);
      }

      if (filterExtra) {
        if (filterSearchText) {
          filter = and(filterSearchText, filterExtra);
        } else {
          filter = filterExtra;
        }
      } else if (filterSearchText) {
        filter = filterSearchText;
      }

      // Add already selected values to filter, so they appear in search results and are not magically gone
      let filterSelectedValues = null;
      if (this.hasValueSelected) {
        filterSelectedValues = this.valueAsList;
      }

      if (filter && filterSelectedValues) {
        const selectedValuesFilter = comparison(this.itemValue, inList(...filterSelectedValues)).toString();
        filter = or(filter, selectedValuesFilter);
      }

      return filter;
    },
    hasValueSelected () {
      return (this.multiple && this.value.length > 0) || (!this.multiple && this.value);
    },
    valueAsList () {
      if (this.multiple) {
        return this.value;
      } else {
        return [this.value];
      }
    }
  },
  methods: {
    searchDelayed() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        this.searchItems();
      }, 400);
    },
    searchItems() {
      if (!this.loading) {
        this.loading = true;

        let params = {
          pageSize: this.pageSize
        }

        if (this.filter) {
          params.filter = this.filter;
        }

        this.$http.get(this.resourceUrl + '/', {params: params})
          .then(response => {
            this.moreItemsAvailable = !response.data.last;
            this.numberOfTotalItems = response.data.totalElements;
            this.loadSelectedItemsIfNeeded(response.data.content)
            .then(items => this.items = items);
          })
          .catch((error) => {
            console.error('Could not load items', error);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    loadIfLazy() {
      if(!this.eagerLoading) {
        if(this.searchText == null || this.searchText.trim() === '') {
          this.searchText = null;
          this.searchItems();
        }
      }
    },
    isItemSelected (selectedItem) {
      if (this.valueAsList) {
        return this.valueAsList.includes(selectedItem[this.itemValue]);
      }
      return false;
    },
    onClear() {
      this.$emit('update:value', null);
      this.searchText = null;
      this.searchItems();
    },
    onInput ($event) {
      this.$emit('update:value', $event);
      this.searchText = null;
      this.searchItems();
    },
    loadSelectedItemsIfNeeded (items) {
      let filterSelectedValues = null;
      if (this.hasValueSelected) {
        filterSelectedValues = this.valueAsList;
      }

      return new Promise((resolve, reject) => {
        if (filterSelectedValues) {
          let requiredItemsToBeLoadedByValue = [];
          filterSelectedValues.forEach(value => {
            let foundItem = items.find(item => item[this.itemValue] === value || item === value);
            if (!foundItem) {
              requiredItemsToBeLoadedByValue.push(value);
            }
          });

          if (requiredItemsToBeLoadedByValue && requiredItemsToBeLoadedByValue.length > 0) {
            this.$http.get(this.resourceUrl, {
              params: {
                filter: comparison(this.itemValue, inList(...requiredItemsToBeLoadedByValue)).toString()
              }
            })
            .then(response => {
              response.data.content.forEach(item => {
                items.unshift(item);
              })
              resolve(items);
            })
            .catch((error) => {
              console.error('Could not load item(s)', error);
              reject(items);
            })
            .finally(() => {
              this.loading = false;
            });
          } else {
            resolve(items);
          }
        } else {
          resolve(items);
        }
      })
    },
    filterSelectedValues (item, queryText, itemText) {
      return queryText && queryText.length > 0
        ? !this.isItemSelected(item)
        : true;
    }
  },
  watch: {
    searchText: {
      handler: function (value) {
        if (typeof value === 'string') {
          this.searchDelayed();
        }
      }
    },
    value: {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.loadSelectedItemsIfNeeded(this.items || []);
        } else {
          if(this.eagerLoading) {
            this.searchText = null;
            this.searchItems();
          }
        }
      }
    }
  }
}
</script>
